
import { getCurrentUserId } from '../user';
import { getProducts, getGroups, getStockUpdates, getUsageRecords } from '../../storage';
import { migrateProducts } from './products';
import { migrateGroups } from './groups';
import { migrateStockUpdates, migrateUsageRecords } from './records';
import { isIosBrowser } from '@/utils/mobileInitializer';

/**
 * Migrate data from localStorage to Supabase
 */
export const migrateLocalStorageToSupabase = async () => {
  try {
    // Apply iOS/Chrome specific timeout workarounds
    const isMobileIOS = isIosBrowser();
    const timeoutValue = isMobileIOS ? 15000 : 8000; // Longer timeout for iOS
    
    console.log(`Starting migration with ${isMobileIOS ? 'iOS-specific' : 'standard'} settings`);
    
    const userId = await Promise.race([
      getCurrentUserId(),
      new Promise((_, reject) => 
        setTimeout(() => reject(new Error('User ID fetch timeout')), timeoutValue)
      )
    ]);
    
    if (!userId) {
      console.error('No user ID available for migration');
      return { 
        success: false, 
        message: 'You need to be logged in to sync data'
      };
    }

    console.log(`Starting migration for user ${userId}`);
    
    // Get data from localStorage - add extra error handling for mobile browsers
    let localProducts = [];
    let localGroups = [];
    let localStockUpdates = [];
    let localUsageRecords = [];
    
    try {
      localProducts = getProducts();
      localGroups = getGroups();
      localStockUpdates = getStockUpdates();
      localUsageRecords = getUsageRecords();
    } catch (storageError) {
      console.error('Error accessing localStorage:', storageError);
      return {
        success: false,
        message: 'Could not access local data. Please try refreshing the page.',
        error: storageError
      };
    }
    
    // Only proceed if there's data to migrate
    const hasData = localProducts.length > 0 || 
                    localGroups.length > 0 || 
                    localStockUpdates.length > 0 || 
                    localUsageRecords.length > 0;
    
    if (!hasData) {
      console.log('No local data to migrate');
      return { 
        success: true, 
        message: 'No local data found to sync',
        results: null
      };
    }
    
    console.log(`Starting migration of ${localProducts.length} products, ${localGroups.length} groups, ${localStockUpdates.length} stock updates, and ${localUsageRecords.length} usage records`);
    
    // Create smaller batches for iOS to prevent timeouts
    const batchSize = isMobileIOS ? 10 : 50;
    
    // Migrate each data type with timeout protection
    const migrateWithTimeout = async (migrationFn, data, name) => {
      try {
        return await Promise.race([
          migrationFn(userId, data),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error(`${name} migration timeout`)), timeoutValue)
          )
        ]);
      } catch (error) {
        console.error(`Error in ${name} migration:`, error);
        return { success: false, count: 0, errors: [error.message] };
      }
    };
    
    const productResults = await migrateWithTimeout(migrateProducts, localProducts, 'products');
    const groupResults = await migrateWithTimeout(migrateGroups, localGroups, 'groups');
    const stockUpdateResults = await migrateWithTimeout(migrateStockUpdates, localStockUpdates, 'stock updates');
    const usageRecordResults = await migrateWithTimeout(migrateUsageRecords, localUsageRecords, 'usage records');
    
    // Report results - Fix: Access nested properties correctly
    const success = productResults.success && 
                    (groupResults.groups?.success || true) && 
                    (groupResults.productGroups?.success || true) && 
                    stockUpdateResults.success && 
                    usageRecordResults.success;
    
    const results = {
      products: productResults,
      groups: groupResults,
      stockUpdates: stockUpdateResults,
      usageRecords: usageRecordResults
    };
    
    // Detailed logging for transparency
    console.log('Migration results:', {
      success,
      products: `${productResults.count}/${localProducts.length} migrated`,
      groups: `${groupResults.groups?.count || 0}/${localGroups.length} migrated`,
      stockUpdates: `${stockUpdateResults.count}/${localStockUpdates.length} migrated`,
      usageRecords: `${usageRecordResults.count}/${localUsageRecords.length} migrated`
    });
    
    // On iOS, consider partial success as success to prevent continuous retries
    const iosPartialSuccess = isMobileIOS && (
      productResults.count > 0 || 
      (groupResults.groups?.count || 0) > 0 || 
      stockUpdateResults.count > 0 || 
      usageRecordResults.count > 0
    );
    
    return {
      success: success || iosPartialSuccess,
      message: success 
        ? `Successfully synced data to the cloud` 
        : iosPartialSuccess 
          ? `Partially synced data to the cloud. Some items may need to be synced again.`
          : `Some data could not be synced. Please try again.`,
      results
    };
  } catch (error: any) {
    console.error('Error in migrateLocalStorageToSupabase:', error);
    
    return {
      success: false,
      message: `Error syncing data: ${error.message || 'Unknown error'}`,
      error
    };
  }
};
